import React, { useEffect, useState, useRef } from "react";
import CustomFileUploader from "./fileSelector.jsx";
import * as XLSX from "xlsx";
import { MdFileUpload, MdPersonAdd } from "react-icons/md";
import { useToast } from "../../../Widgets/Toast";
import { uploadFileCustom } from "../Scripts/fileUpload.js";
import Cookies from "js-cookie";
import { userService } from "../Scripts/UserService";
import { environment } from "@/env/env.prod";

export function getExcelSheetAttributes(file) {
  return new Promise((resolve, reject) => {
    if (!(file instanceof Blob)) {
      reject("Invalid file type. Must be a File or Blob.");
      return;
    }

    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const firstSheetName = workbook.SheetNames[ 0 ];
        const worksheet = workbook.Sheets[ firstSheetName ];
        const sheetData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
        if (sheetData.length > 0) resolve(sheetData[ 0 ]);
        else reject("Sheet is empty");
      } catch (err) {
        reject("Failed to read file: " + err.message);
      }
    };

    reader.onerror = () => reject("Failed to load file");
    reader.readAsArrayBuffer(file);
  });
}

// Most users one add-user file may contain. Keep equal to MAX_BULK_USERS in the server's
// env/environment.js — the server enforces it too and refuses a bigger file whole.
export const MAX_BULK_USERS = 500;

// Number of data rows in a sheet file, counted the way the server counts them: every sheet's
// rows below its header, ignoring completely blank lines.
export function countSheetRows(file) {
  return new Promise((resolve, reject) => {
    if (!(file instanceof Blob)) { reject("Invalid file type. Must be a File or Blob."); return; }
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const workbook = XLSX.read(new Uint8Array(e.target.result), { type: "array" });
        let total = 0;
        for (const name of workbook.SheetNames) {
          const rows = XLSX.utils.sheet_to_json(workbook.Sheets[ name ], { header: 1 });
          total += rows.slice(1).filter((row) => Array.isArray(row) && row.some((cell) => String(cell ?? "").trim() !== "")).length;
        }
        resolve(total);
      } catch (err) {
        reject("Failed to read file: " + err.message);
      }
    };
    reader.onerror = () => reject("Failed to load file");
    reader.readAsArrayBuffer(file);
  });
}

function createJsonFile(data, fileName = "data.json") {
  const json = JSON.stringify(data, null, 2);
  const blob = new Blob([ json ], { type: "application/json" });
  return new File([ blob ], fileName, { type: "application/json" });
}

// ─── TODO: Fill in URL and params when ready ─────────────────────────────────

async function submitIndividual({ rollId, email, operateOn }) {
  return await userService.addUser(rollId, email, operateOn);
}
// ─────────────────────────────────────────────────────────────────────────────

function IndividualAddForm({ operateOn, operationType, navigationIndex, showToast }) {
  const [ email, setEmail ] = useState("");
  const [ rollId, setRollId ] = useState("");
  const [ isSubmitting, setIsSubmitting ] = useState(false);

  const entityLabel = operateOn === 1 ? "Teacher" : "Student";
  const idLabel = operateOn === 1 ? "Employee ID" : "Roll Number";
  const idPlaceholder = operateOn === 1 ? "e.g. EMP-2024-042" : "e.g. STU-2024-001";
  const isAdminAdd = navigationIndex === 13;

  function validate() {
    if (!rollId.trim()) { showToast(`Please enter a ${idLabel}`, "error"); return false; }
    if (!email.trim()) { showToast("Please enter an email address", "error"); return false; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      showToast("Please enter a valid email address", "error");
      return false;
    }
    return true;
  }

  async function handleSubmit() {
    if (!validate()) return;
    setIsSubmitting(true);
    try {
      const result = await submitIndividual({ rollId: rollId.trim(), email: email.trim(), operateOn });
      showToast(`${entityLabel} added successfully!`, "success");
      setEmail("");
      setRollId("");
    } catch (e) {
      console.error("Individual add error:", e);
      showToast(e.message || "Something went wrong!", "error");
    } finally {
      setIsSubmitting(false);
    }
  }

  const inputStyle = {
    display: "block", width: "100%", marginTop: "6px",
    padding: "10px 14px", borderRadius: "8px",
    border: "1px solid #ccc", fontSize: "14px",
    boxSizing: "border-box", background: "#ffffff", color: "#000000",
  };

  const labelStyle = { color: "#aaa", fontSize: "13px", fontWeight: 600, display: "block" };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "18px" }}>
      <div>
        <label style={labelStyle}>{idLabel}</label>
        <input
          value={rollId}
          onChange={(e) => setRollId(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
          placeholder={idPlaceholder}
          style={inputStyle}
        />
      </div>
      <div>
        <label style={labelStyle}>Email Address</label>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
          placeholder={`e.g. ${operateOn === 1 ? "teacher" : "student"}@school.edu`}
          style={inputStyle}
        />
      </div>
      <div style={{ display: "flex", justifyContent: "center", marginTop: "4px" }}>
        <button
          onClick={handleSubmit}
          disabled={isSubmitting}
          style={{
            display: "flex", alignItems: "center", gap: "8px",
            background: "#ffcb77", border: "none", padding: "10px 24px",
            borderRadius: "8px", fontWeight: 600, fontSize: "14px",
            cursor: isSubmitting ? "not-allowed" : "pointer",
            opacity: isSubmitting ? 0.65 : 1, transition: "opacity 0.2s",
          }}
        >
          <MdPersonAdd style={{ fontSize: "20px" }} />
          {isSubmitting ? "Adding..." : `Add ${entityLabel}`}
        </button>
      </div>
    </div>
  );
}

export function CSVOperationsManager({ navigationIndex, options = {} }) {

  const [ activeTab, setActiveTab ] = useState("csv");

  const [ selectedOption, setSelectedOption ] = useState("<- Select Primary Key ->");
  const [ selectEmail, setEmailOption ] = useState("<- Select Email Key ->");
  const [ dropdownOptions, setDropdownOptions ] = useState([ "<- Select Primary Key ->" ]);
  const [ selectedFile, setSelectedFile ] = useState(null);
  const [ rowCount, setRowCount ] = useState(null); // data rows in the picked file (add-user files only)
  const [ header, setHeader ] = useState();
  const { showToast } = useToast();

  const { onFilePickup = undefined, modifyJson = undefined } = options;

  const [ operateOn, setOperationEntity ] = useState();
  const [ operationType, setOperationType ] = useState();
  const [ heading, SetHeading ] = useState("");

  const [ resetOpen, setResetOpen ] = useState(false);
  const [ resetStudentId, setResetStudentId ] = useState("");
  const [ isResetting, setIsResetting ] = useState(false);
  const [ showAlumniSwitch, setAlumniSwitch ] = useState()
  const [ alumniEnabled, setAlumniEnabled ] = useState(true);
  const alumniSwitchRef = useRef(true);

  let heading_1 = "";
  let heading_2 = "";

  useEffect(() => {

    if ([ 1, 2, 3, 4, 12, 13, 14 ].includes(navigationIndex)) {
      setOperationEntity(1);
      heading_2 = "Teachers";
    } else if ([ 5, 6, 7, 8 ].includes(navigationIndex)) {
      setOperationEntity(0);
      heading_2 = "Students";
    } else if (navigationIndex === 23) {
      setOperationEntity(0);
      heading_2 = "Students";
    }

    if (navigationIndex === 4 || navigationIndex === 8) {
      setOperationType("push_data");
      heading_1 = "Message";
    } else if (navigationIndex === 3 || navigationIndex === 7 || navigationIndex === 14) {
      setOperationType("del_id");
      heading_1 = "Delete";
    } else if (navigationIndex === 6 || navigationIndex === 2 || navigationIndex === 13) {
      setOperationType("add_id");
      heading_1 = "Add";
    } else if (navigationIndex === 23) {
      setOperationType("reset_dep");
      heading_1 = "Reset Departments";
    }

    if (navigationIndex === 12 || navigationIndex === 13 || navigationIndex === 14) {
      heading_2 = "Admins";
    }

    if ((navigationIndex === 3 || navigationIndex === 7 || navigationIndex === 14) && ([ 5, 6, 7, 8 ].includes(navigationIndex))) {
      setAlumniSwitch(true)
    }

    SetHeading(heading_1 + " " + heading_2);

  }, []);

  async function handleResetSingle() {
    if (!resetStudentId.trim()) return;
    setIsResetting(true);
    try {
      const res = await fetch(
        `${environment.BASE_URL}/Change_Student_Department?` +
        new URLSearchParams({
          parent: Cookies.get("ins_code") || "",
          E_id: Cookies.get("E_id") || "",
          AuthToken: Cookies.get("authToken") || "",
          student_id: resetStudentId.trim(),
        }),
        { method: "GET" }
      );
      if (res.status === 200) {
        showToast("Department reset successfully!", "success");
        setResetOpen(false);
        setResetStudentId("");
      } else {
        showToast(`Failed — status ${res.status}`, "error");
      }
    } catch (e) {
      console.error("Reset error:", e);
      showToast("Something went wrong", "error");
    } finally {
      setIsResetting(false);
    }
  }

  function createJson() {
    if (selectedOption === "<- Select Primary Key ->") {
      showToast("Please select the primary key!", "error");
      return;
    }

    if (operationType === 'add_id' && selectEmail === "<- Select Email Key ->") {
      showToast("Please select email key!", "error");
      return;
    }

    let jsonMap = {
      key: selectedOption,
      Facalty: operateOn,
      email: operationType === 'add_id' ? selectEmail : undefined
    };

    if (navigationIndex === 13) {
      jsonMap[ "admin_add" ] = 1;
    }

    if (typeof modifyJson === "function") {
      const updatedFields = modifyJson();
      jsonMap.fields = updatedFields;
    }

    if ((navigationIndex === 3 || navigationIndex === 7 || navigationIndex === 14) && ([ 5, 6, 7, 8 ].includes(navigationIndex))) {
      jsonMap[ "add_to_alumni" ] = alumniSwitchRef.current;
    }

    const filename = selectedFile.name.replace(/\.[^/.]+$/, "") + ".json";
    const jsonBlob = new Blob([ JSON.stringify(jsonMap, null, 2) ], { type: "application/json" });
    const jsonFile = new File([ jsonBlob ], filename, { type: "application/json" });

    const authToken = Cookies.get("authToken");
    const UID = Cookies.get("user_uid");
    const ins_code = Cookies.get("ins_code");

    uploadFileCustom(jsonFile, `${ins_code}/csv/${operationType}/${UID}/${filename}`, authToken)
      .then(() => showToast("JSON uploaded successfully!", "success"))
      .catch(() => showToast("Upload failed!", "error"));

    uploadFileCustom(selectedFile, `${ins_code}/csv/${operationType}/${UID}/${selectedFile.name}`, authToken)
      .then(() => showToast(
        operationType === "add_id"
          ? "File uploaded. A report will be emailed to you when processing finishes."
          : "CSV uploaded successfully!",
        "success"))
      .catch(() => showToast("Upload failed!", "error"));
  }

  function uploadFile() {
    if (!selectedFile) {
      showToast("No file was selected !", "error");
      return;
    }
    if (operationType === "add_id" && rowCount !== null && rowCount > MAX_BULK_USERS) {
      showToast(`This file has ${rowCount} rows. You can add at most ${MAX_BULK_USERS} users per file. Please split it into smaller files.`, "error");
      return;
    }
    createJson();
  }

  const filePickupManager = (file) => {
    if (!(file instanceof Blob)) {
      console.error("Received non-file input");
      return;
    }

    setSelectedFile(file);
    setRowCount(null);
    if (operationType === "add_id") {
      countSheetRows(file)
        .then((count) => {
          setRowCount(count);
          if (count > MAX_BULK_USERS) {
            showToast(`This file has ${count} rows. You can add at most ${MAX_BULK_USERS} users per file. Please split it into smaller files.`, "error");
          }
        })
        .catch((err) => console.error("Failed to count rows:", err));
    }

    getExcelSheetAttributes(file)
      .then((headers) => {
        setDropdownOptions([ "<- Select Primary Key ->", ...headers ]);
        setSelectedOption("<- Select Primary Key ->");
        if (onFilePickup) onFilePickup(file, headers);
      })
      .catch((err) => console.error("Failed to extract headers:", err));
  };

  const selectStyle = {
    padding: "10px 14px",
    borderRadius: "8px",
    border: "1px solid #444",
    background: "#ffffff",
    color: "#000000",
    fontSize: "14px",
    minWidth: "220px"
  };

  const showIndividualTab = operationType === "add_id";

  return (
    <div style={{
      padding: "32px",
      backgroundColor: "#fff5e0",
      borderRadius: "16px",
      border: "1px solid #e6d5b8",
      boxShadow: "0 6px 18px rgba(0,0,0,0.12)",
      width: "100%",
      maxWidth: "1500px",
      margin: "0 auto",
    }}>

      <div style={{ marginBottom: "20px" }}>
        <h2 style={{ color: "#000000", fontWeight: 600, fontSize: "20px", marginBottom: "4px" }}>
          {heading}
        </h2>
        <p style={{ color: "#888", fontSize: "13px" }}>
          {activeTab === "csv"
            ? "Upload CSV/XLSX file and select the primary key column."
            : "Enter individual details to add a single record."}
        </p>
      </div>

      {/* Tab Bar — only for add operations */}
      {showIndividualTab && (
        <div style={{
          display: "flex", gap: "4px", marginBottom: "24px",
          background: "#f5e9d0", borderRadius: "10px", padding: "4px",
        }}>
          {[ { id: "csv", label: "Bulk CSV / XLSX" }, { id: "individual", label: "Add Individual" } ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                flex: 1, padding: "8px 16px", borderRadius: "8px", border: "none",
                background: activeTab === tab.id ? "#ffcb77" : "transparent",
                fontWeight: activeTab === tab.id ? 700 : 500,
                fontSize: "13px", cursor: "pointer", color: "#000",
                transition: "background 0.2s",
                boxShadow: activeTab === tab.id ? "0 2px 6px rgba(0,0,0,0.1)" : "none",
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>
      )}

      {/* ── CSV Tab ── */}
      {activeTab === "csv" && (
        <>
          {/* Primary Key Row */}
          <div style={{ display: "flex", alignItems: "center", gap: "20px", marginBottom: "12px" }}>
            <label style={{ color: "#aaa", fontSize: "14px", minWidth: "120px", textAlign: "left" }}>
              Primary Key
            </label>
            <select value={selectedOption} onChange={(e) => setSelectedOption(e.target.value)} style={selectStyle}>
              {dropdownOptions.map((option, index) => (
                <option key={index} value={option} disabled={index === 0}>{option}</option>
              ))}
            </select>
          </div>

          {/* Email Column Row (Conditional) */}
          {operationType === 'add_id' && (
            <div style={{ display: "flex", alignItems: "center", gap: "20px", marginBottom: "20px" }}>
              <label style={{ color: "#aaa", fontSize: "14px", minWidth: "120px", textAlign: "left" }}>
                Email Column
              </label>
              <select value={selectEmail} onChange={(e) => setEmailOption(e.target.value)} style={selectStyle}>
                {[ "<- Select Email Key ->", ...dropdownOptions.slice(1) ].map((opt, i) => (
                  <option key={i} value={opt} disabled={i === 0}>{opt}</option>
                ))}
              </select>
            </div>
          )}

          {/* Alumni Settings */}
          {showAlumniSwitch === true && (
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              marginBottom: "20px", padding: "14px 18px", border: "1px solid #e6d5b8",
              borderRadius: "14px", background: "#fffaf0", maxWidth: "500px",
            }}>
              <div style={{ display: "flex", flexDirection: "column" }}>
                <span style={{ color: "#1f2937", fontSize: "15px", fontWeight: "600" }}>Add to Alumni Directory</span>
                <span style={{ color: "#6b7280", fontSize: "12px", marginTop: "3px" }}>Imported students will appear inside alumni directory</span>
              </div>
              <div
                onClick={() => {
                  const newValue = !alumniEnabled;
                  setAlumniEnabled(newValue);
                  alumniSwitchRef.current = newValue;
                }}
                style={{
                  width: "54px", height: "30px", borderRadius: "999px",
                  background: alumniEnabled ? "#ffcb77" : "#d1d5db",
                  position: "relative", cursor: "pointer", transition: "0.25s ease",
                  display: "flex", alignItems: "center", padding: "4px", boxSizing: "border-box",
                }}
              >
                <div style={{
                  width: "22px", height: "22px", borderRadius: "50%", background: "#fff",
                  transform: alumniEnabled ? "translateX(24px)" : "translateX(0px)",
                  transition: "0.25s ease", boxShadow: "0 2px 6px rgba(0,0,0,0.18)",
                }} />
              </div>
            </div>
          )}

          <div style={{ marginBottom: "20px" }}>
            <CustomFileUploader callback={filePickupManager} />
            {operationType === "add_id" && rowCount !== null && (
              <p style={{ margin: "10px 0 0", fontSize: "13px", color: rowCount > MAX_BULK_USERS ? "#b82b2b" : "#6b7280", fontWeight: rowCount > MAX_BULK_USERS ? 600 : 400 }}>
                {rowCount > MAX_BULK_USERS
                  ? `${rowCount} rows found — over the limit of ${MAX_BULK_USERS} users per file. Split it into smaller files to upload.`
                  : `${rowCount} row${rowCount === 1 ? "" : "s"} found (limit ${MAX_BULK_USERS} per file).`}
              </p>
            )}
          </div>

          <div style={{ display: "flex", justifyContent: "center" }}>
            <button
              onClick={uploadFile}
              style={{
                display: "flex", alignItems: "center", gap: "8px",
                background: "#ffcb77", border: "none", padding: "10px 18px",
                borderRadius: "8px", fontWeight: 600, cursor: "pointer"
              }}
            >
              <MdFileUpload style={{ fontSize: "20px" }} />
              Upload
            </button>
          </div>

          {navigationIndex === 23 && (
            <div style={{ display: "flex", justifyContent: "center", marginTop: "12px" }}>
              <button
                onClick={() => setResetOpen(true)}
                style={{
                  display: "flex", alignItems: "center", gap: "8px",
                  background: "#fff", border: "2px solid #ffcb77",
                  padding: "10px 18px", borderRadius: "8px",
                  fontWeight: 600, cursor: "pointer", color: "#000"
                }}
              >
                Reset Single Student
              </button>
            </div>
          )}
        </>
      )}

      {/* ── Individual Tab ── */}
      {activeTab === "individual" && (
        <IndividualAddForm
          operateOn={operateOn}
          operationType={operationType}
          navigationIndex={navigationIndex}
          showToast={showToast}
        />
      )}

      {/* Reset Single Student Modal */}
      {resetOpen && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: "rgba(0,0,0,0.4)",
          display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000
        }}>
          <div style={{
            backgroundColor: "#fff5e0", borderRadius: "16px", border: "1px solid #e6d5b8",
            boxShadow: "0 8px 24px rgba(0,0,0,0.15)", padding: "32px", width: "100%", maxWidth: "400px",
          }}>
            <h3 style={{ margin: "0 0 8px 0", fontWeight: 700, fontSize: "18px" }}>Reset Student Department</h3>
            <p style={{ color: "#888", fontSize: "13px", marginBottom: "20px" }}>
              This will clear the student's current department so they can re-select one.
            </p>
            <label style={{ color: "#aaa", fontSize: "13px", fontWeight: 600 }}>Student ID</label>
            <input
              value={resetStudentId}
              onChange={(e) => setResetStudentId(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleResetSingle()}
              placeholder="e.g. STU-2024-001"
              autoFocus
              style={{
                display: "block", width: "100%", marginTop: "6px", marginBottom: "20px",
                padding: "10px 14px", borderRadius: "8px", border: "1px solid #ccc",
                fontSize: "14px", boxSizing: "border-box"
              }}
            />
            <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
              <button
                onClick={() => { setResetOpen(false); setResetStudentId(""); }}
                disabled={isResetting}
                style={{ padding: "9px 18px", borderRadius: "8px", border: "1px solid #ccc", background: "#fff", cursor: "pointer", fontWeight: 600 }}
              >
                Cancel
              </button>
              <button
                onClick={handleResetSingle}
                disabled={isResetting || !resetStudentId.trim()}
                style={{
                  padding: "9px 18px", borderRadius: "8px", border: "none", background: "#ffcb77",
                  cursor: "pointer", fontWeight: 600,
                  opacity: isResetting || !resetStudentId.trim() ? 0.6 : 1
                }}
              >
                {isResetting ? "Resetting..." : "Reset"}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}